'use client';

import type { Erc721SingleAllowance, TokenAllowanceData } from '@revoke.cash/core/allowances';
import { isNullish } from '@revoke.cash/core/utils';
import { type RowSelectionState, useTable } from '@tanstack/react-table';
import { allowancesTableFeatures, ColumnId, columns } from 'components/allowances/dashboard/columns';
import Table from 'components/common/table/Table';
import { useAddressAllowances } from 'lib/hooks/page-context/AddressPageContext';
import { updateTableFilters } from 'lib/utils/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AllowanceTableControls from './controls/AllowanceTableControls';
import NoAllowancesFound from './NoAllowancesFound';

const getRowId = (row: TokenAllowanceData) => {
  return `${row.chainId}-${row.token.address}-${row.payload.spender}-${(row.payload as Erc721SingleAllowance).tokenId}`;
};

const AllowanceDashboard = () => {
  const { allowances, isLoading, error, onUpdate } = useAddressAllowances();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // We fall back to an empty array because the table crashes if the data is undefined
  // and we use useMemo to prevent the table from infinite re-rendering
  const data = useMemo(() => {
    return allowances ?? [];
  }, [allowances]);

  // When rows are deleted, the row selection state is not updated automatically (see https://github.com/TanStack/table/issues/4369)
  // This effect manually syncs the row selection state with the new table data
  useEffect(() => {
    setRowSelection((currentSelection: RowSelectionState) => {
      if (!data || data.length === 0) return {};
      if (Object.keys(currentSelection).length === 0) return {};

      return data.reduce<RowSelectionState>((acc, allowance) => {
        if (currentSelection[getRowId(allowance)]) acc[getRowId(allowance)] = true;
        return acc;
      }, {});
    });
  }, [data]);

  const table = useTable({
    features: allowancesTableFeatures,
    data,
    columns,
    state: {
      rowSelection,
    },
    enableRowSelection: (row) => isNullish(row.original.payload.revokeError),
    onRowSelectionChange: setRowSelection,
    autoResetPageIndex: false,
    getRowId,
    meta: { onUpdate },
    initialState: {
      sorting: [{ id: ColumnId.LAST_UPDATED, desc: true }],
      columnVisibility: {
        [ColumnId.BALANCE]: false,
      },
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: table is stable
  const onSearchValuesChange = useCallback((values: string[]) => {
    const tableFilters = values.length > 0 ? [{ id: ColumnId.SPENDER, value: values }] : [];
    const ignoreIds = Object.values(ColumnId).filter((id) => id !== ColumnId.SPENDER);
    updateTableFilters(table, tableFilters, ignoreIds);
  }, []);

  return (
    <div className="flex flex-col justify-start mx-auto gap-2">
      <AllowanceTableControls onSearchValuesChange={onSearchValuesChange} onSortChange={table.setSorting} />
      <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl">
        <Table
          table={table}
          loading={isLoading}
          error={error}
          emptyChildren={<NoAllowancesFound allowances={allowances!} />}
          className="border-none"
        />
      </div>
    </div>
  );
};

export default AllowanceDashboard;
